/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import type { KeyboardEvent } from 'react';

import { useIntl } from 'react-intl';

import { cssMap, jsx } from '@atlaskit/css';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Flex } from '@atlaskit/primitives/compiled/flex';
import { token } from '@atlaskit/tokens';

import { messages } from './messages';

const styles = cssMap({
	inputWrapper: {
		display: 'flex',
		alignItems: 'center',
		flexGrow: 1,
		backgroundColor: token('color.background.input'),
		borderRadius: token('radius.large'),
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: token('color.border.input'),
		paddingBlock: token('space.100'),
		paddingInlineStart: token('space.150'),
		paddingInlineEnd: token('space.100'),
	},
	input: {
		flexGrow: 1,
		border: 'none',
		backgroundColor: 'transparent',
		outline: 'none',
		font: token('font.body'),
		color: token('color.text'),
	},
});

type InputFieldProps = {
	value: string;
	onChange: (value: string) => void;
	onKeyDown: (e: KeyboardEvent<HTMLInputElement>) => void;
	placeholder?: string;
	disabled?: boolean;
	testId?: string;
};

export const InputField = ({
	value,
	onChange,
	onKeyDown,
	placeholder,
	disabled = false,
	testId = 'input-field',
}: InputFieldProps): JSX.Element => {
	const { formatMessage } = useIntl();

	return (
		<Flex xcss={styles.inputWrapper} testId={testId}>
			<Box
				as="input"
				type="text"
				xcss={styles.input}
				value={value}
				onChange={(e) => onChange(e.target.value)}
				onKeyDown={onKeyDown}
				placeholder={placeholder}
				disabled={disabled}
				aria-label={formatMessage(messages.chatMessageInputLabel)}
			/>
		</Flex>
	);
};
